import { useState, useEffect, useCallback, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import {
  Dumbbell, CreditCard, AlertTriangle, Calendar, Clock,
  CheckCircle2, XCircle, ShoppingBag, TrendingUp, User, Users,
  MapPin, Loader2, Package, ArrowRight, Info, Bell, Trophy, Flame, Star, Target, Megaphone, Pin
} from 'lucide-react';

const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function AlunoDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmando, setConfirmando] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const navigate = useNavigate();
  const signatureRef = useRef(null);

  const aceitarContrato = async () => {
    try {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        toast.error('Assine no campo indicado antes de continuar.');
        return;
      }

      const assinatura = signatureRef.current.toDataURL('image/png');

      await api.post('/aluno/aceitar-contrato', {
        assinatura
      });

      toast.success('Contrato assinado com sucesso!');
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, avisosRes] = await Promise.all([
        api.get('/aluno/dashboard'),
        api.get('/aluno/avisos')
      ]);

      setData(dashboardRes.data);
      setAvisos(avisosRes.data || []);

      const novos = (avisosRes.data || [])
        .filter(aviso => aviso.novo);

      if (novos.length) {
        Promise.allSettled(
          novos.map(aviso =>
            api.post(`/aluno/avisos/${aviso.id}/visualizar`)
          )
        );
      }
    } catch {
      /* silent */
    }

    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const confirmarTreino = async (treinoId) => {
    setConfirmando(treinoId);
    try {
      await api.post(`/aluno/treinos/${treinoId}/confirmar`);
      toast.success('Presença confirmada!');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setConfirmando(null);
  };

  if (loading) {
  
  const calcularMaiorDeIdade = (dataNascimento) => {
    if (!dataNascimento) return false;

    const nascimento = new Date(dataNascimento);
    const hoje = new Date();

    let idade = hoje.getFullYear() - nascimento.getFullYear();

    const fezAniversario =
      hoje.getMonth() > nascimento.getMonth() ||
      (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() >= nascimento.getDate());

    if (!fezAniversario) idade--;

    return idade >= 18;
  };

  const maiorDeIdade = calcularMaiorDeIdade(data?.aluno?.data_nascimento);

  return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64 bg-[#141414]" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 bg-[#141414]" />)}
        </div>
        <Skeleton className="h-48 bg-[#141414]" />
      </div>
    );
  }

  const d = data || {};
  const proximo = d.proximos_treinos?.[0];
  const pm = d.proxima_mensalidade;
  const medalhas = d.gamificacao?.medalhas || [];
  // XP vem do backend: mesma fonte usada pelo ranking.
  const xp = Number(d.xp_total || 0);
  const nivel = Math.max(1, Math.floor(xp / 200) + 1);
  const xpNivelAtual = xp % 200;
  const progresso = Math.min(100, Math.round((xpNivelAtual / 200) * 100));

  const conquistas = [
    d.aluno?.contrato_aceito ? 'Contrato assinado' : null,
    !d.inadimplente ? 'Atleta em dia' : null,
    d.proximos_treinos?.some(t => t.confirmado) ? 'Presença confirmada' : null
  ].filter(Boolean);

  const streak = d.gamificacao?.streak || 0;

  const rankingTurma = d.gamificacao?.ranking_turma?.length
    ? d.gamificacao.ranking_turma
    : [
        { nome: d.aluno?.name || 'Você', xp, voce: true }
      ];



  const calcularMaiorDeIdade = (dataNascimento) => {
    if (!dataNascimento) return false;

    const nascimento = new Date(dataNascimento);
    const hoje = new Date();

    let idade = hoje.getFullYear() - nascimento.getFullYear();

    const fezAniversario =
      hoje.getMonth() > nascimento.getMonth() ||
      (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() >= nascimento.getDate());

    if (!fezAniversario) idade--;

    return idade >= 18;
  };

  const maiorDeIdade = calcularMaiorDeIdade(data?.aluno?.data_nascimento);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="aluno-dashboard">
      <Dialog open={data?.aluno && data.aluno.contrato_aceito !== true}>
        <DialogContent className="bg-[#141414] border-[#27272A] text-white max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl tracking-tight uppercase">
              Contrato e autorização
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              {maiorDeIdade ? (
                <>
                  <p className="text-sm text-green-300 font-semibold">Este atleta é maior de idade.</p>
                  <p className="text-sm text-zinc-300 mt-1">O termo deve ser aceito pelo próprio atleta.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-amber-300 font-semibold">Este atleta é menor de idade.</p>
                  <p className="text-sm text-zinc-300 mt-1">O termo deve ser aceito pelo responsável legal.</p>
                </>
              )}
            </div>

            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 space-y-3 text-sm text-zinc-300 leading-relaxed">
              <p className="font-semibold text-white">Termo de participação e autorização</p>
              <p>Declaro estar ciente e de acordo com a participação do atleta nas atividades esportivas, treinos, eventos internos e demais ações promovidas pela instituição.</p>
              <p>Autorizo o uso de imagem do atleta em fotos e vídeos relacionados às atividades esportivas, publicações institucionais, redes sociais e materiais de divulgação.</p>
              <p>Declaro estar ciente das regras de conduta, horários, mensalidades, responsabilidades, cuidados pessoais e orientações repassadas pela equipe responsável.</p>
              <p>
                {maiorDeIdade
                  ? 'Ao assinar digitalmente este termo, confirmo que sou o próprio atleta e estou ciente das condições de participação.'
                  : 'Ao assinar digitalmente este termo, confirmo que sou responsável legal pelo atleta ou estou autorizado a realizar este aceite.'}
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
                {maiorDeIdade ? 'Assinatura do atleta' : 'Assinatura do responsável legal'}
              </p>
              <div className="bg-white rounded-xl overflow-hidden border border-[#27272A]">
                <SignatureCanvas
                  ref={signatureRef}
                  penColor="black"
                  canvasProps={{
                    width: 620,
                    height: 180,
                    className: "w-full h-[180px]"
                  }}
                />
              </div>

              <div className="flex gap-2 mt-2">
                <Button type="button" variant="outline" onClick={() => signatureRef.current?.clear()} className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E]">
                  Limpar assinatura
                </Button>
              </div>
            </div>

            <Button onClick={aceitarContrato} className="w-full bg-[var(--cor-primaria,#007AFF)] hover:opacity-90">
              Assinar e continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <section
        className="relative overflow-hidden rounded-3xl border border-white/[0.08] p-5 sm:p-6 lg:p-7"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--cor-primaria, #007AFF) 22%, #111114) 0%, #111114 48%, color-mix(in srgb, var(--cor-secundaria, #111827) 18%, #09090B) 100%)'
        }}
      >
        <div
          className="absolute -right-16 -top-24 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
        />

        <div className="relative flex items-start justify-between gap-5 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
              />
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.22em] font-bold text-zinc-400">
                Meu espaço esportivo
              </p>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white leading-none">
              Olá, {d.aluno?.name?.split(' ')[0] || 'Atleta'}!
            </h1>

            <p className="text-sm text-zinc-400 mt-2">
              Acompanhe seus treinos, evolução e conquistas.
            </p>

            <div className="flex items-center gap-2 flex-wrap mt-4">
              <Badge
                className="border"
                style={{
                  color: 'var(--cor-primaria, #007AFF)',
                  borderColor:
                    'color-mix(in srgb, var(--cor-primaria, #007AFF) 35%, transparent)',
                  backgroundColor:
                    'color-mix(in srgb, var(--cor-primaria, #007AFF) 12%, transparent)'
                }}
              >
                <Star className="w-3.5 h-3.5 mr-1" />
                Nível {nivel}
              </Badge>

              {d.notificacoes_nao_lidas > 0 && (
                <Badge className="bg-red-500/10 text-red-300 border-red-500/20">
                  <Bell className="w-3.5 h-3.5 mr-1" />
                  {d.notificacoes_nao_lidas} aviso(s)
                </Badge>
              )}
            </div>
          </div>

          {d.aluno?.contrato_pdf_url && (
            <Button
              size="sm"
              variant="outline"
              className="border-white/10 bg-black/10 text-zinc-300 hover:bg-white/10 hover:text-white"
              onClick={() => window.open(d.aluno.contrato_pdf_url, '_blank')}
            >
              Baixar contrato
            </Button>
          )}
        </div>
      </section>


      {avisos.length > 0 && (
        <section
          className="space-y-3"
          data-testid="aluno-avisos"
        >
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[var(--cor-primaria,#007AFF)]" />

            <h2 className="text-xl font-black font-['Barlow_Condensed'] uppercase text-white">
              Avisos
            </h2>

            {avisos.some(aviso => aviso.novo) && (
              <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                Novo
              </Badge>
            )}
          </div>

          <div className="space-y-4">
            {avisos.map(aviso => (
              <Card
                key={aviso.id}
                className={`bg-[#141414] overflow-hidden ${
                  aviso.fixado
                    ? 'border-amber-500/30'
                    : 'border-[#27272A]'
                }`}
              >
                {aviso.imagem_url && (
                  <div className="bg-black">
                    <img
                      src={aviso.imagem_url}
                      alt={aviso.imagem_legenda || aviso.titulo}
                      className="w-full max-h-[420px] object-cover"
                    />
                  </div>
                )}

                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {aviso.novo && (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px]">
                            Novo
                          </Badge>
                        )}

                        {aviso.fixado && (
                          <span className="text-xs text-amber-400 flex items-center gap-1">
                            <Pin className="w-3 h-3" />
                            Fixado
                          </span>
                        )}
                      </div>

                      <h3 className="text-xl font-black font-['Barlow_Condensed'] text-white">
                        {aviso.titulo}
                      </h3>
                    </div>
                  </div>

                  {aviso.imagem_legenda && (
                    <p className="text-xs italic text-zinc-500 mt-2">
                      {aviso.imagem_legenda}
                    </p>
                  )}

                  {aviso.mensagem && (
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed mt-3">
                      {aviso.mensagem}
                    </p>
                  )}

                  <p className="text-[11px] text-zinc-600 mt-4">
                    {new Date(aviso.publicar_em).toLocaleString(
                      'pt-BR',
                      {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      }
                    )}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* TREINO PRINCIPAL + RESUMO */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_0.65fr] gap-4">

        {/* PRÓXIMO TREINO EM DESTAQUE */}
        <Card
          className="relative overflow-hidden bg-[#111114] border-white/[0.08] rounded-2xl cursor-pointer group"
          onClick={() => navigate('/aluno/treinos')}
          data-testid="card-proximo-treino"
        >
          <div
            className="absolute inset-y-0 left-0 w-1"
            style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
          />

          <div
            className="absolute -right-20 -bottom-24 w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
          />

          <CardContent className="relative p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                  Próximo treino
                </p>

                {proximo ? (
                  <h3 className="text-3xl sm:text-4xl font-black font-['Barlow_Condensed'] text-white uppercase leading-none mt-1">
                    {proximo.nome}
                  </h3>
                ) : (
                  <h3 className="text-2xl font-black font-['Barlow_Condensed'] text-zinc-400 uppercase mt-1">
                    Nenhum treino agendado
                  </h3>
                )}
              </div>

              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--cor-primaria, #007AFF) 14%, transparent)'
                }}
              >
                <Dumbbell
                  className="w-6 h-6"
                  style={{ color: 'var(--cor-primaria, #007AFF)' }}
                />
              </div>
            </div>

            {proximo && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                  <div className="flex items-center gap-3 rounded-xl bg-black/20 border border-white/[0.06] p-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{
                        backgroundColor:
                          'color-mix(in srgb, var(--cor-primaria, #007AFF) 12%, transparent)'
                      }}
                    >
                      <Calendar
                        className="w-4 h-4"
                        style={{ color: 'var(--cor-primaria, #007AFF)' }}
                      />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-600">Data</p>
                      <p className="text-sm font-semibold text-white">{proximo.data}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 rounded-xl bg-black/20 border border-white/[0.06] p-3">
                    <div className="w-9 h-9 rounded-lg bg-white/[0.05] flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-zinc-300" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-600">Horário</p>
                      <p className="text-sm font-semibold text-white">
                        {proximo.horario}
                        {proximo.horario_fim ? ` – ${proximo.horario_fim}` : ''}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-x-5 gap-y-2 flex-wrap mt-4 text-sm text-zinc-400">
                  {proximo.local && (
                    <span className="flex items-center gap-1.5">
                      <MapPin
                        className="w-4 h-4"
                        style={{ color: 'var(--cor-primaria, #007AFF)' }}
                      />
                      {proximo.local}
                    </span>
                  )}

                  {proximo.turma_nome && (
                    <span className="flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-zinc-500" />
                      {proximo.turma_nome}
                    </span>
                  )}

                  {proximo.professor_nome && (
                    <span className="flex items-center gap-1.5">
                      <User className="w-4 h-4 text-zinc-500" />
                      {proximo.professor_nome}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap mt-5 pt-4 border-t border-white/[0.06]">
                  <span className="text-xs text-zinc-500">
                    Acesse Treinos para ver todos os detalhes
                  </span>

                  {proximo.encerrado ? (
                    <Badge className="bg-zinc-700/30 text-zinc-400 border-zinc-700/40">
                      Encerrado
                    </Badge>
                  ) : proximo.confirmado ? (
                    <Badge
                      className="bg-green-500/10 text-green-400 border-green-500/20"
                      data-testid="proximo-confirmado-badge"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      Presença confirmada
                    </Badge>
                  ) : !d.inadimplente ? (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmarTreino(proximo.id);
                      }}
                      disabled={confirmando === proximo.id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="btn-eu-vou-dashboard"
                    >
                      {confirmando === proximo.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1.5" />
                          Eu vou
                        </>
                      )}
                    </Button>
                  ) : (
                    <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                      <XCircle className="w-3.5 h-3.5 mr-1" />
                      Inapto
                    </Badge>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* RESUMO RÁPIDO */}
        <div className="grid grid-cols-3 xl:grid-cols-1 gap-3">
          <Card className="bg-[#111114] border-white/[0.08] rounded-2xl">
            <CardContent className="p-4 flex xl:items-center xl:justify-between gap-3">
              <div>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-bold">
                  Sequência
                </p>
                <p className="text-xl sm:text-2xl font-black font-['Barlow_Condensed'] text-white">
                  {streak}
                  <span className="text-xs text-zinc-500 font-sans font-normal ml-1">treinos</span>
                </p>
              </div>
              <Flame className="hidden sm:block w-5 h-5 text-orange-400 shrink-0" />
            </CardContent>
          </Card>

          <Card className="bg-[#111114] border-white/[0.08] rounded-2xl">
            <CardContent className="p-4 flex xl:items-center xl:justify-between gap-3">
              <div>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-bold">
                  Presença
                </p>
                <p className="text-xl sm:text-2xl font-black font-['Barlow_Condensed'] text-white">
                  {d.pct_presenca || 0}%
                </p>
              </div>
              <TrendingUp className="hidden sm:block w-5 h-5 text-green-400 shrink-0" />
            </CardContent>
          </Card>

          <Card className="bg-[#111114] border-white/[0.08] rounded-2xl">
            <CardContent className="p-4 flex xl:items-center xl:justify-between gap-3">
              <div>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-bold">
                  XP total
                </p>
                <p className="text-xl sm:text-2xl font-black font-['Barlow_Condensed'] text-white">
                  {xp}
                </p>
              </div>
              <Star
                className="hidden sm:block w-5 h-5 shrink-0"
                style={{ color: 'var(--cor-primaria, #007AFF)' }}
              />
            </CardContent>
          </Card>
        </div>
      </div>


      {/* ATALHOS RÁPIDOS */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
              Acesso rápido
            </p>
            <h2 className="text-xl font-black font-['Barlow_Condensed'] uppercase text-white mt-0.5">
              Meu esporte
            </h2>
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 md:grid md:grid-cols-5 md:overflow-visible">
          <button
            type="button"
            onClick={() => navigate('/aluno/treinos')}
            className="group min-w-[150px] md:min-w-0 text-left rounded-2xl bg-[#111114] border border-white/[0.08] p-4 hover:border-white/[0.16] transition-all"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--cor-primaria, #007AFF) 14%, transparent)'
              }}
            >
              <Calendar
                className="w-5 h-5"
                style={{ color: 'var(--cor-primaria, #007AFF)' }}
              />
            </div>
            <p className="text-sm font-bold text-white">Meus Treinos</p>
            <p className="text-[11px] text-zinc-500 mt-1">Agenda presencial</p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/aluno/treinos-casa')}
            className="group min-w-[150px] md:min-w-0 text-left rounded-2xl bg-[#111114] border border-white/[0.08] p-4 hover:border-white/[0.16] transition-all"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--cor-primaria, #007AFF) 14%, transparent)'
              }}
            >
              <Dumbbell
                className="w-5 h-5"
                style={{ color: 'var(--cor-primaria, #007AFF)' }}
              />
            </div>
            <p className="text-sm font-bold text-white">Treino em Casa</p>
            <p className="text-[11px] text-zinc-500 mt-1">Exercícios e séries</p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/aluno/ranking')}
            className="group min-w-[150px] md:min-w-0 text-left rounded-2xl bg-[#111114] border border-white/[0.08] p-4 hover:border-white/[0.16] transition-all"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--cor-primaria, #007AFF) 14%, transparent)'
              }}
            >
              <Trophy
                className="w-5 h-5"
                style={{ color: 'var(--cor-primaria, #007AFF)' }}
              />
            </div>
            <p className="text-sm font-bold text-white">Ranking</p>
            <p className="text-[11px] text-zinc-500 mt-1">Veja sua posição</p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/aluno/medalhas')}
            className="group min-w-[150px] md:min-w-0 text-left rounded-2xl bg-[#111114] border border-white/[0.08] p-4 hover:border-white/[0.16] transition-all"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--cor-primaria, #007AFF) 14%, transparent)'
              }}
            >
              <Star
                className="w-5 h-5"
                style={{ color: 'var(--cor-primaria, #007AFF)' }}
              />
            </div>
            <p className="text-sm font-bold text-white">Medalhas</p>
            <p className="text-[11px] text-zinc-500 mt-1">Suas conquistas</p>
          </button>

          <button
            type="button"
            onClick={() => navigate('/aluno/mensalidades')}
            className="group min-w-[150px] md:min-w-0 text-left rounded-2xl bg-[#111114] border border-white/[0.08] p-4 hover:border-white/[0.16] transition-all"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--cor-primaria, #007AFF) 14%, transparent)'
              }}
            >
              <CreditCard
                className="w-5 h-5"
                style={{ color: 'var(--cor-primaria, #007AFF)' }}
              />
            </div>
            <p className="text-sm font-bold text-white">Mensalidades</p>
            <p className="text-[11px] text-zinc-500 mt-1">Pagamentos e situação</p>
          </button>
        </div>
      </section>

      <Card className="relative overflow-hidden bg-[#111114] border-white/[0.08] rounded-2xl">
        <div
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{ backgroundColor: 'var(--cor-primaria, #007AFF)' }}
        />

        <CardContent className="p-5 sm:p-6">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                Minha evolução
              </p>

              <div className="flex items-end gap-3 mt-1">
                <h2 className="text-4xl font-black font-['Barlow_Condensed'] text-white uppercase leading-none">
                  Nível {nivel}
                </h2>

                <span className="text-xs text-zinc-500 mb-1">
                  {xpNivelAtual} / 200 XP
                </span>
              </div>
            </div>

            <div className="text-right">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">
                {progresso}%
              </p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-600">
                próximo nível
              </p>
            </div>
          </div>

          <div className="mt-5 h-2.5 bg-black/40 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progresso}%`,
                background:
                  'linear-gradient(90deg, var(--cor-primaria, #007AFF), var(--cor-secundaria, #60A5FA))'
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-bold mb-2">Medalhas</p>
              <div className="flex flex-wrap gap-2">
                {medalhas.length > 0 ? medalhas.map((m, i) => (
                  <Badge key={i} title={m.descricao} className="bg-yellow-500/10 text-yellow-300 border-yellow-500/20">
                    <span className="mr-1">{m.icone}</span> {m.nome}
                  </Badge>
                )) : (
                  <Badge className="bg-[#007AFF]/10 text-[#60A5FA] border-[#007AFF]/20">
                    <Star className="w-3.5 h-3.5 mr-1" /> Em evolução
                  </Badge>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-bold mb-2">Missões da semana</p>
              <div className="space-y-2 text-sm text-zinc-300">
                <div className="flex items-center gap-2"><Target className="w-4 h-4 text-green-400" /> Confirmar presença no próximo treino</div>
                <div className="flex items-center gap-2"><Target className="w-4 h-4 text-green-400" /> Manter mensalidade em dia</div>
                <div className="flex items-center gap-2"><Target className="w-4 h-4 text-green-400" /> Evoluir 200 XP para subir de nível</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RANKING COMPACTO */}
      <Card className="bg-[#111114] border-white/[0.08] rounded-2xl">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-bold">
                Ranking da turma
              </p>
              <h3 className="text-2xl font-black font-['Barlow_Condensed'] text-white uppercase">
                Top atletas
              </h3>
            </div>

            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-zinc-400 hover:text-white"
              onClick={() => navigate('/aluno/ranking')}
            >
              Ver ranking
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {rankingTurma.slice(0, 3).map((a, i) => (
              <div
                key={i}
                className={`flex items-center justify-between rounded-xl px-3 py-3 border ${
                  a.voce
                    ? 'border-[var(--cor-primaria,#007AFF)]/30 bg-[var(--cor-primaria,#007AFF)]/10'
                    : 'border-white/[0.06] bg-black/20'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center text-xs font-black text-white shrink-0">
                    #{i + 1}
                  </div>

                  <p className="text-sm font-medium text-white truncate">
                    {a.nome}
                  </p>
                </div>

                <Badge className="ml-2 shrink-0 bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                  {a.xp} XP
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ALERTAS */}
      {d.alertas?.length > 0 && (
        <div className="space-y-2" data-testid="aluno-alertas">
          {d.alertas.map((a, i) => (
            <Card key={i} className={`border ${a.tipo === 'danger' ? 'bg-red-500/5 border-red-500/20' : a.tipo === 'warning' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-blue-500/5 border-blue-500/20'}`}>
              <CardContent className="p-3 flex items-center gap-3">
                {a.tipo === 'danger' ? <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" /> :
                 a.tipo === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" /> :
                 <Info className="w-4 h-4 text-blue-400 shrink-0" />}
                <p className={`text-sm ${a.tipo === 'danger' ? 'text-red-400' : a.tipo === 'warning' ? 'text-amber-400' : 'text-blue-400'}`}>{a.msg}</p>
                {a.tipo === 'danger' && d.inadimplente && (
                  <Button size="sm" className="ml-auto bg-red-600 hover:bg-red-700 shrink-0 text-xs" onClick={() => navigate('/aluno/mensalidades')} data-testid="aluno-regularizar-btn">
                    Regularizar
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* CARDS PRINCIPAIS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* MINHA MENSALIDADE */}
        <Card className={`border transition-all duration-300 cursor-pointer ${pm?.status === 'atrasado' ? 'bg-red-500/5 border-red-500/20 hover:border-red-500/40' : 'bg-[#141414] border-[#27272A] hover:border-amber-500/30'}`} onClick={() => navigate('/aluno/mensalidades')} data-testid="card-mensalidade">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Mensalidade</p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${pm?.status === 'atrasado' ? 'bg-red-500/10' : d.mensalidades_pendentes > 0 ? 'bg-amber-500/10' : 'bg-green-500/10'}`}>
                <CreditCard className={`w-4 h-4 ${pm?.status === 'atrasado' ? 'text-red-400' : d.mensalidades_pendentes > 0 ? 'text-amber-400' : 'text-green-400'}`} />
              </div>
            </div>
            {pm ? (
              <div>
                <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">{fmt(pm.valor)}</p>
                {(() => {
                    const vencimento = pm?.data_vencimento;
                    const hoje = new Date();
                    const venc = new Date(vencimento + 'T00:00:00');
                    const dias = Math.ceil((venc - hoje) / (1000 * 60 * 60 * 24));

                    let cor = "text-green-400";
                    if (dias <= 1) cor = "text-red-400";
                    else if (dias <= 5) cor = "text-yellow-400";

                    let texto = "";
                    if (dias < 0) texto = `Vencida há ${Math.abs(dias)} dia(s)`;
                    else if (dias === 0) texto = "Seu vencimento é hoje";
                    else if (dias === 1) texto = "Falta 1 dia para o seu vencimento";
                    else texto = `Faltam ${dias} dias para o seu vencimento`;

                    return <p className={`text-xs mt-1 ${cor}`}>{texto}</p>;
                  })()}
                <div className="mt-2 flex items-center justify-between">
                  <Badge className={pm.status === 'atrasado' ? 'bg-red-400/10 text-red-400 border-red-400/20' : 'bg-amber-400/10 text-amber-400 border-amber-400/20'}>
                    {pm.status === 'atrasado' ? 'Vencida' : 'Pendente'}
                  </Badge>
                  <span className="text-xs text-[var(--cor-primaria,#007AFF)] font-medium">Pagar <ArrowRight className="w-3 h-3 inline" /></span>
                </div>
              </div>
            ) : (
              <div>
  <p className="text-lg font-bold text-green-400">Em dia</p>

  {(() => {
    const dia = d?.aluno?.dia_vencimento;

    if (!dia) {
    
  const calcularMaiorDeIdade = (dataNascimento) => {
    if (!dataNascimento) return false;

    const nascimento = new Date(dataNascimento);
    const hoje = new Date();

    let idade = hoje.getFullYear() - nascimento.getFullYear();

    const fezAniversario =
      hoje.getMonth() > nascimento.getMonth() ||
      (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() >= nascimento.getDate());

    if (!fezAniversario) idade--;

    return idade >= 18;
  };

  const maiorDeIdade = calcularMaiorDeIdade(data?.aluno?.data_nascimento);

  return (
        <p className="text-xs text-zinc-500">
          Sem mensalidades pendentes
        </p>
      );
    }

    const hoje = new Date();

    let venc = new Date(
      hoje.getFullYear(),
      hoje.getMonth(),
      dia
    );

    if (hoje.getDate() > dia) {
      venc = new Date(
        hoje.getFullYear(),
        hoje.getMonth() + 1,
        dia
      );
    }

    const dias = Math.ceil(
      (venc - hoje) / (1000 * 60 * 60 * 24)
    );

    let cor = "text-green-400";

    if (dias <= 1) cor = "text-red-400";
    else if (dias <= 5) cor = "text-yellow-400";

    let texto = "";

    if (dias === 0) {
      texto = "Seu vencimento é hoje";
    } else if (dias === 1) {
      texto = "Falta 1 dia para o seu vencimento";
    } else {
      texto = `Faltam ${dias} dias para o seu vencimento`;
    }

  
  const calcularMaiorDeIdade = (dataNascimento) => {
    if (!dataNascimento) return false;

    const nascimento = new Date(dataNascimento);
    const hoje = new Date();

    let idade = hoje.getFullYear() - nascimento.getFullYear();

    const fezAniversario =
      hoje.getMonth() > nascimento.getMonth() ||
      (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() >= nascimento.getDate());

    if (!fezAniversario) idade--;

    return idade >= 18;
  };

  const maiorDeIdade = calcularMaiorDeIdade(data?.aluno?.data_nascimento);

  return (
      <p className={`text-xs mt-1 ${cor}`}>
        {texto}
      </p>
    );
  })()}
</div>
            )}
          </CardContent>
        </Card>

        {/* LOJA */}
        <Card className="bg-[#141414] border-[#27272A] hover:border-purple-500/30 transition-all duration-300 cursor-pointer" onClick={() => navigate('/aluno/loja')} data-testid="card-loja">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Loja</p>
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 text-purple-400" />
              </div>
            </div>
            {d.produtos_destaque?.length > 0 ? (
              <div>
                <div className="flex -space-x-2 mb-2">
                  {d.produtos_destaque.slice(0, 3).map((p, i) => (
                    <div key={p.id} className="w-10 h-10 rounded-lg bg-[#0A0A0A] border-2 border-[#141414] overflow-hidden flex items-center justify-center" style={{ zIndex: 3 - i }}>
                      {p.imagem_url ? <img src={p.imagem_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-zinc-600" />}
                    </div>
                  ))}
                  {d.produtos_destaque.length > 3 && (
                    <div className="w-10 h-10 rounded-lg bg-[#27272A] border-2 border-[#141414] flex items-center justify-center text-xs text-zinc-400">+{d.produtos_destaque.length - 3}</div>
                  )}
                </div>
                <p className="text-sm font-medium text-white">{d.produtos_destaque.length} produto(s)</p>
                <span className="text-xs text-purple-400 font-medium mt-1 inline-block">Ver loja <ArrowRight className="w-3 h-3 inline" /></span>
              </div>
            ) : (
              <div>
                <Package className="w-8 h-8 text-zinc-700 mb-2" />
                <p className="text-sm text-zinc-600">Nenhum produto</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PROXIMOS TREINOS */}
      {d.proximos_treinos?.length > 1 && (
        <div data-testid="dashboard-proximos-treinos">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm tracking-[0.15em] uppercase font-bold text-zinc-500">Proximos Treinos</h2>
            <Button size="sm" variant="ghost" className="text-xs text-zinc-400 hover:text-white" onClick={() => navigate('/aluno/treinos')}>
              Ver todos <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {d.proximos_treinos.slice(1, 4).map(t => (
              <Card key={t.id} className="bg-[#141414] border-[#27272A] hover:border-[#3f3f46] transition-all" data-testid={`dash-treino-${t.id}`}>
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg shrink-0 flex flex-col items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--cor-primaria, #007AFF) 10%, transparent)' }}>
                      <span className="text-[10px] font-bold" style={{ color: 'var(--cor-primaria, #007AFF)' }}>{t.data?.split('-')[2]}</span>
                      <span className="text-[8px] text-zinc-500">{['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(t.data?.split('-')[1]) - 1]}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{t.nome}</p>
                      <p className="text-xs text-zinc-500">{t.horario} {t.turma_nome ? `| ${t.turma_nome}` : ''}</p>
                    </div>
                  </div>
                  <div className="shrink-0">
                    {t.confirmado ? (
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">Eu vou</Badge>
                    ) : t.encerrado ? (
                      <Badge className="bg-zinc-700/30 text-zinc-500 border-zinc-700/40 text-xs">Encerrado</Badge>
                    ) : !d.inadimplente ? (   
                     <Badge className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20 text-xs">
                         Indisponível
                     </Badge>
                            ) : (
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">Inapto</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
